import * as React from 'react';
import { Input } from '@gje/forms/lib/input';
import { Field, InjectedFormProps, reduxForm } from 'redux-form';
import { PrimaryButton } from '@gje/buttons/lib/primary-button';
import { getText } from '../../../../services/text/text-service';
import {
    addValidator,
    format,
    length,
    required,
    validateForm
} from 'redux-form-validators';
import { connect } from 'react-redux';
import { BodyText, Text, Title } from '@gje/typography/lib';
import {
    isIOS,
    isIPad13,
    isMobile,
    isSafari,
    isTablet
} from 'react-device-detect';
import { ageResolver } from '../../../../services/customer/customer-service';
import classNames from 'classnames';
import { cleanError } from '../../../../actions/error-actions';
import {
    createUtagViewData,
    invokeFbq,
    invokeUtagData
} from '../../../../utils/statisticsUtils';
import { resolvePartnerForStats } from '../../../../services/stats/stats-service';
import { StatsDataEnum } from '../../../../types/enums/statsDataEnum';
import Personnummer from 'personnummer';
import { cin } from 'swe-validation';
import { HtmlPanel } from '../../../components/HtmlPanel/HtmlPanel';

const cprAndAgeValidator = addValidator({
    validator: (options, value, allValues) => {
        value = value.replace(/-/g, '');

        if (!(Personnummer.valid(value) || cin(value).isValid)) {
            return {
                defaultMessage: 'Vänligen kontrollera organisationsnummer'
            };
        } else {
            if (ageResolver(value) < options.age) {
                return {
                    defaultMessage:
                        'Du måste vara över 18 år för att köpa en försäkring'
                };
            } else return '';
        }
    }
});

let validate = validateForm({
    cpr: [
        required({ msg: 'Obligatoriskt fält' }),
        cprAndAgeValidator({ age: 18 })
    ],
    regPlate: [
        required({ msg: 'Obligatoriskt fält' }),
        length({
            msg: 'Vänligen kontrollera registreringsnumret',
            is: 6
        }),
        format({
            msg: 'Vänligen kontrollera registreringsnumret',
            with: /([A-Z]{3})([0-9]{2})([0-9A-Z])/
        })
    ]
});

interface StateToProps {
    calculationData: any;
}

interface DispatchToProps {
    cleanError: Function;
}

class FirstStep extends React.Component<
    InjectedFormProps & StateToProps & DispatchToProps
> {
    componentWillUnmount(): void {
        window.sessionStorage.removeItem('company_car_cpr');
        window.sessionStorage.removeItem('company_car_reg_plate');
    }

    renderInput({ input, data, meta }) {
        return (
            <div className="col-sm-9">
                <Input
                    onChange={input.onChange}
                    labelText={data.label}
                    id={data.label}
                    name={input.name}
                    onBlur={input.onBlur}
                    errorText={meta.error}
                    value={input.value.toUpperCase()}
                    invalid={meta.touched && meta.error}
                    placeholder={data.placeHolder}
                    type={data.type}
                    inputClassName={
                        isIOS || isSafari ? 'apple-input-line-height' : ''
                    }
                />
            </div>
        );
    }

    intervalUtag = setInterval(() => {
        //@ts-ignore
        if (window.utag) {
            invokeUtagData(
                createUtagViewData(
                    StatsDataEnum.APPLICATION_NAME,
                    'company-car',
                    resolvePartnerForStats(),
                    window.location.pathname +
                        'company-car/' +
                        StatsDataEnum.CALCULATION,
                    StatsDataEnum.PAGE_TITLE,
                    'tjanstebilforsakring',
                    StatsDataEnum.CALCULATION
                )
            );
            clearInterval(this.intervalUtag);
        }
    }, 500);

    intervalFbq = setInterval(() => {
        if (window.location.host.includes('dev' || 'test')) {
            clearInterval(this.intervalFbq);
        }
        //@ts-ignore
        if (window.fbq) {
            invokeFbq('tjanstebilforsakring');
            clearInterval(this.intervalFbq);
        }
    }, 500);

    regNumberFieldData = {
        label: getText('webshopsweden.products.car.carPlates'),
        placeHolder: getText('webshopsweden.products.car.carPlatesPlaceHolder'),
        type: 'text'
    };

    orgNoFieldData = {
        label: getText('webshopsweden.common.organisationNumber'),
        type: 'text'
    };

    cprOnChange(value: any) {
        return value.replace(/\D/g, '');
    }

    render() {
        const { handleSubmit } = this.props;
        const mobileView = window.innerWidth <= 921 || isMobile;
        return (
            <div className={'first-step-padding'}>
                <Title
                    tag={mobileView ? 'h4' : 'h2'}
                    children={getText(
                        'webshopsweden.products.car.calculatePriceHeader'
                    )}
                    className={classNames(
                        'only-xs-margin-bottom-26',
                        mobileView ? 'margin-left-13' : 'margin-left-30',
                        isTablet || isIPad13 ? 'margin-bottom-50' : ''
                    )}
                />
                <div className="row">
                    <div
                        className={classNames('col-sm-23 ', {
                            'margin-left-50': !mobileView
                        })}>
                        <form
                            className={classNames(
                                'row',
                                mobileView ? '' : 'margin-bottom-top-80'
                            )}
                            onSubmit={handleSubmit}>
                            <div className={'only-xs-margin-bottom-26'}>
                                <Field
                                    name="regPlate"
                                    data={this.regNumberFieldData}
                                    component={this.renderInput}
                                />
                            </div>
                            <div>
                                <Field
                                    name="cpr"
                                    data={this.orgNoFieldData}
                                    component={this.renderInput}
                                    parse={this.cprOnChange}
                                />
                            </div>
                            <div className="col-sm-5">
                                <PrimaryButton
                                    borderColor={'red'}
                                    type="submit"
                                    className={classNames(
                                        'first-step-button',
                                        isTablet || isIPad13
                                            ? 'tablet-calculate-button'
                                            : ''
                                    )}
                                    children={getText(
                                        'webshopsweden.common.calculatePriceButton'
                                    )}
                                    onClick={() => {
                                        this.props.cleanError();
                                    }}
                                />
                            </div>
                        </form>
                        <div
                            className={classNames('row', {
                                'first-step-contact-info': !mobileView,
                                'margin-top-20': isTablet || isIPad13
                            })}>
                            <div className="col-sm-13">
                                <BodyText className="small-text">
                                    <HtmlPanel>
                                        {getText(
                                            'webshopsweden.products.gjensidige.webshops.personal.data'
                                        )}
                                    </HtmlPanel>
                                </BodyText>
                            </div>
                        </div>
                        <div
                            className={classNames('row margin-top-13', {
                                'first-step-back-button': !mobileView
                            })}>
                            <div className="col-sm-5">
                                <a
                                    href={'/'}
                                    onClick={() => this.props.cleanError()}>
                                    <Text>
                                        {getText('webshopsweden.common.back')}
                                    </Text>
                                </a>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        );
    }
}

const mapStateToProps = state => ({
    firstStepForm: state.form.firstStep,
    calculationData: state.calculationData
});

const Connected = connect(mapStateToProps, { cleanError })(FirstStep);

export default reduxForm<any>({
    form: 'firstStep',
    destroyOnUnmount: true,
    validate
})(Connected);
